/**
 * Table formatters - Utility functions for formatting data in tables
 * These formatters provide consistent formatting across all table views
 */

// =============================================================================
// DATE FORMATTERS
// =============================================================================

export type DateFormatType = 'date' | 'datetime' | 'time' | 'relative' | 'short';

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday")
 */
const formatRelativeDate = (date: Date, locale = 'fr-FR'): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffDay > 30) {
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  if (diffDay >= 1) {
    return rtf.format(-diffDay, 'day');
  }
  if (diffHour >= 1) {
    return rtf.format(-diffHour, 'hour');
  }
  if (diffMin >= 1) {
    return rtf.format(-diffMin, 'minute');
  }
  return rtf.format(-diffSec, 'second');
};

/**
 * Format a date string according to the specified format type
 * @param dateString - The date string to format
 * @param formatType - The format type to use
 * @param locale - The locale to use for formatting (default: 'fr-FR')
 * @returns Formatted date string or '-' if invalid
 */
export const formatDate = (
  dateString: string | Date | null | undefined,
  formatType: DateFormatType = 'date',
  locale = 'fr-FR',
): string => {
  if (!dateString) return '-';

  const date = dateString instanceof Date ? dateString : new Date(dateString);

  if (Number.isNaN(date.getTime())) return '-';

  switch (formatType) {
    case 'date':
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

    case 'datetime':
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

    case 'time':
      return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      });

    case 'short':
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short',
      });

    case 'relative':
      return formatRelativeDate(date, locale);

    default:
      return date.toLocaleDateString(locale);
  }
};

// =============================================================================
// NUMBER FORMATTERS
// =============================================================================

/**
 * Format a number as a percentage
 * @param value - The value to format (0-1 or 0-100)
 * @param isDecimal - Whether the value is already a decimal (0-1) or percentage (0-100)
 * @param decimals - Number of decimal places
 */
export const formatPercentage = (
  value: number | null | undefined,
  isDecimal = true,
  decimals = 2,
): string => {
  if (value === null || value === undefined) return '-';

  const numValue = isDecimal ? value : value / 100;

  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(numValue);
};

/**
 * Format a number as currency
 * @param value - The value to format
 * @param currency - The currency code (default: 'EUR')
 * @param locale - The locale to use
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency = 'EUR',
  locale = 'fr-FR',
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format an amount with currency (handles string/number conversion)
 * @param amount - The amount to format (can be string or number)
 * @param currency - The currency code (default: 'EUR')
 * @param locale - The locale to use (default: 'fr-FR')
 * @returns Formatted amount string or '-' if invalid
 */
export const formatAmount = (
  amount: string | number | null | undefined,
  currency: string | null | undefined = 'EUR',
  locale = 'fr-FR',
): string => {
  if (!amount || amount === 'null' || amount === 'undefined') return '-';

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(numAmount)) return '-';

  const curr = currency && currency !== 'null' && currency !== 'undefined' ? currency : 'EUR';

  try {
    return numAmount.toLocaleString(locale, {
      style: 'currency',
      currency: curr,
    });
  } catch (error) {
    return `${numAmount} ${curr}`;
  }
};

/**
 * Format a number with decimal places
 * @param value - The value to format
 * @param decimals - Number of decimal places
 */
export const formatDecimal = (
  value: number | null | undefined,
  decimals = 2,
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format a number as an integer (no decimal places)
 * @param value - The value to format
 */
export const formatInteger = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format a large number with abbreviations (K, M, B)
 * @param value - The value to format
 */
export const formatCompact = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
};

// =============================================================================
// TEXT FORMATTERS
// =============================================================================

/**
 * Format a full name from first and last name
 * @param firstName - First name
 * @param lastName - Last name
 */
export const formatFullName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '';
};

/**
 * Truncate text with ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 */
export const truncateText = (
  text: string | null | undefined,
  maxLength = 50,
): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitalize first letter of a string
 * @param text - The text to capitalize
 */
export const capitalizeFirst = (
  text: string | null | undefined,
): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};
